import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, Mail } from "lucide-react";
import Logo from "@/components/Logo";
import { firstAllowedPath } from "@/lib/permissions";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = "Yönetim Paneli - Renka"; }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(firstAllowedPath(u), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Zaten girişliyse login ekranını gösterme (geri tuşu senaryosu) — yalnızca yönetici rolleri
  if (user && user.role && user.role !== "parent") {
    return <Navigate to={firstAllowedPath(user)} replace />;
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-slate-950 px-4">
      {/* Ambient gradient blobs */}
      <div className="absolute -top-40 -left-40 h-96 w-96 rounded-full bg-violet-600/30 blur-[120px]" />
      <div className="absolute -bottom-40 -right-40 h-96 w-96 rounded-full bg-blue-600/30 blur-[120px]" />
      <div className="absolute top-1/3 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-fuchsia-500/20 blur-[120px]" />

      <div className="relative z-10 w-full max-w-md" data-testid="login-page">
        {/* Brand */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-5 inline-flex items-center justify-center rounded-2xl bg-white px-6 py-4 shadow-lg shadow-violet-900/30">
            <Logo className="h-12 w-auto object-contain" alt="RENKA ÖZEL EĞİTİM" data-testid="login-logo" />
          </div>
          <p className="text-sm font-medium tracking-wide text-slate-300">Özel Eğitim · Yönetim Paneli</p>
        </div>

        {/* Glass card */}
        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-white/10 bg-white/[0.06] p-8 backdrop-blur-2xl shadow-[0_8px_40px_rgba(0,0,0,0.4)]"
        >
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-300">
                E-posta
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@admin.com"
                  required
                  className="h-12 pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-violet-500"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-300">
                Şifre
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="h-12 pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-violet-500"
                />
              </div>
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm font-medium text-red-400">
                {error}
              </p>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="h-12 w-full rounded-xl bg-violet-600 text-base font-semibold text-white transition-colors hover:bg-violet-700 active:scale-[0.98]"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Giriş Yap"}
            </Button>
          </div>
        </form>

        <p className="mt-6 text-center text-xs text-slate-500">
          © 2026 Renka Özel Eğitim Merkezi
        </p>
      </div>
    </div>
  );
}
